import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const INCLUSIVE_LEADERSHIP_PACK: FrameworkPackDefinition = {
  id: "inclusive_leadership",
  label: "Inclusive Leadership",
  family: "coach_custom",
  source: "primary_source",
  preview: {
    philosophySummary:
      "Inclusive Leadership helps leaders turn difference into contribution by increasing belonging, fairness, voice, curiosity, and shared decision quality.",
    whatItNotices: [
      "Whether people with different identities, roles, styles, or levels of power have real voice in the room.",
      "Where bias, default assumptions, or insider patterns may be narrowing who contributes or who is believed.",
      "Whether collaboration produces psychological safety and better decisions, not just participation optics.",
    ],
    watchouts: [
      "Do not reduce inclusion to warmth or representation without decision influence.",
      "Do not infer bias from a single thin transcript moment without observable evidence.",
      "Do not make the leader responsible for diagnosing protected-class dynamics from incomplete context.",
    ],
    sampleCoachLanguage: [
      "Whose perspective was missing or easy to discount in that moment?",
      "What would courage and curiosity look like if you wanted more people to shape the decision?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to notice who had real voice, not only who was present.",
      "Through this lens, inclusion becomes visible when different perspectives change the conversation or decision.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Commitment",
      "Courage",
      "Cognizance of bias",
      "Curiosity",
      "Cultural intelligence",
      "Collaboration",
      "Belonging",
      "Voice",
      "Fairness",
    ],
    diagnosticQuestions: [
      "Who had meaningful voice in this moment, and whose perspective was missing, minimized, or taken for granted?",
      "Where might the leader need more curiosity or cultural intelligence before deciding?",
      "What observable behavior would make belonging and fairness more real in the next conversation?",
    ],
    interventionPatterns: [
      "Name the missing or quiet perspective without accusing motive.",
      "Slow the decision long enough to test default assumptions.",
      "Convert inclusion into one observable meeting behavior: invite, credit, check impact, or share power.",
    ],
    progressMarkers: [
      "The leader actively seeks perspectives that differ from the dominant room view.",
      "People with less power have visible influence on the discussion or decision.",
      "The leader notices default assumptions and tests them before acting.",
    ],
    stallSignals: [
      "Inclusion is described as tone or kindness but does not change who influences the work.",
      "The leader invites input but rewards only familiar perspectives.",
      "Bias is either overclaimed from thin evidence or avoided when the pattern is observable.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "voice_and_belonging_gap",
        label: "Voice and belonging gap",
        description: "Some people are present but do not appear to have meaningful voice or influence.",
        positiveIndicators: ["voice", "belonging", "invite", "included", "perspective"],
        negativeIndicators: ["quiet", "ignored", "missing", "dominated", "discounted"],
        evidenceHints: ["who spoke", "whose idea changed the decision", "who was invited back in"],
        prohibitedInferences: ["protected-class status", "intentional exclusion", "bias diagnosis"],
      },
      {
        id: "assumption_test_needed",
        label: "Assumption test needed",
        description: "A default assumption may be narrowing whose view is trusted or what options are considered.",
        positiveIndicators: ["assumption", "curious", "different view", "check", "context"],
        negativeIndicators: ["default", "same voices", "rush", "dismissed", "insider"],
        evidenceHints: ["tested the assumption", "asked whose view was missing", "slowed the decision"],
        prohibitedInferences: ["motive claim", "unconscious bias diagnosis", "culture judgment"],
      },
    ],
    reflectionPatterns: [
      {
        id: "voice_influence_and_assumptions",
        label: "Voice, influence, and assumptions",
        promptTemplates: [
          "Who had real voice in this moment, and who may have been present without influence?",
          "What assumption did you test, or need to test, before moving forward?",
          "What is one behavior that would make inclusion observable in the next room?",
        ],
        useWhen: ["voice_and_belonging_gap", "assumption_test_needed"],
        avoidWhen: ["thin_evidence", "single_identity_claim", "protected_class_inference"],
      },
    ],
    boundaries: {
      allowed: ["voice", "belonging", "fairness", "assumption testing", "decision influence", "collaboration quality"],
      prohibited: ["protected-class inference", "bias diagnosis from thin evidence", "intent claim", "diversity score"],
      coachOnly: ["explore identity dynamics", "design inclusion repair", "coach bias interruption practices"],
    },
    salienceWeights: {
      voice_and_belonging_gap: 1.5,
      assumption_test_needed: 1.4,
    },
  },
};
